import { forwardRef, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { getSvgPath } from 'figma-squircle';
import { ModelDropdownButtonUI } from '~/design-system/components/PlanningChat/ui/ModelDropdownButtonUI';
import { ChatInputAreaUI } from '~/design-system/components/PlanningChat/ui/ChatInputAreaUI';
import { FileUploadButtonUI } from '~/design-system/components/PlanningChat/ui/FileUploadButtonUI';
import { WebSearchToggleButtonUI } from '~/design-system/components/PlanningChat/ui/WebSearchToggleButtonUI';
import { SendButtonUI } from '~/design-system/components/PlanningChat/ui/SendButtonUI';
import { cn } from '~/lib/utils';
import styles from './PlaygroundChatBar.module.css';

export interface PlaygroundChatBarProps {
  inputValue: string;
  onInputChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  modelLabel?: string;
  modelLeadIconName?: string;
  webSearchEnabled?: boolean;
  onWebSearchToggle?: (next: boolean) => void;
  onAddClick?: () => void;
  sendDisabled?: boolean;
  onSend?: () => void;
  /** Exact Figma sizes (node 3651-9546). The card grows with content; the squircle follows. */
  width?: number;
  minHeight?: number;
  cornerRadius?: number;
  cornerSmoothing?: number;
  className?: string;
}

/**
 * PlaygroundChatBar — a detached copy of the product chat bar for the FeaturePlaygroundExperiment.
 * Reuses the real leaf elements (model dropdown, input, plus, web toggle, send) but owns its own
 * chrome so playground-only cosmetics (iOS corner smoothing, plus/web/submit order) never touch the
 * original `ChatInputBarUI`. The card auto-grows with typed text and the squircle is recomputed to match.
 */
export const PlaygroundChatBar = forwardRef<HTMLTextAreaElement, PlaygroundChatBarProps>(
  function PlaygroundChatBar(
    {
      inputValue,
      onInputChange = () => {},
      placeholder = 'What did you accomplish today?',
      modelLabel = 'Gemini 3.7 Flash',
      modelLeadIconName = 'google-mark',
      webSearchEnabled = false,
      onWebSearchToggle,
      onAddClick,
      sendDisabled,
      onSend,
      width = 416,
      minHeight = 116,
      cornerRadius = 24,
      cornerSmoothing = 0.6,
      className,
    },
    ref,
  ) {
    const cardRef = useRef<HTMLDivElement>(null);
    const [cardHeight, setCardHeight] = useState(minHeight);

    // Keep the squircle in sync with the card's real height as text grows.
    useLayoutEffect(() => {
      const el = cardRef.current;
      if (!el) return;
      const update = () => setCardHeight(el.offsetHeight);
      update();
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return () => ro.disconnect();
    }, []);

    const clipPath = useMemo(
      () => getSvgPath({ width, height: cardHeight, cornerRadius, cornerSmoothing }),
      [width, cardHeight, cornerRadius, cornerSmoothing],
    );
    const resolvedSendDisabled = sendDisabled ?? inputValue.trim().length === 0;

    return (
      <div className={cn(styles.shadow, className)}>
        <div
          ref={cardRef}
          className={styles.card}
          style={{ width, minHeight, clipPath: `path('${clipPath}')` }}
        >
          <div className={styles.middle}>
            <ChatInputAreaUI
              ref={ref}
              value={inputValue}
              placeholder={placeholder}
              onChange={onInputChange}
            />
          </div>

          <div className={styles.bottom}>
            <div className={styles.bottomLeft}>
              <ModelDropdownButtonUI
                label={modelLabel}
                leadIconName={modelLeadIconName}
                leadIconPreserveColors
              />
            </div>

            <div className={styles.bottomRight}>
              <div className={styles.actions}>
                <FileUploadButtonUI onAddImagesClick={onAddClick} onAttachFilesClick={onAddClick} />
                <span className={styles.webSlot} data-playground="web-button">
                  <WebSearchToggleButtonUI enabled={webSearchEnabled} onToggle={onWebSearchToggle} />
                </span>
              </div>
              <SendButtonUI disabled={resolvedSendDisabled} onClick={onSend} />
            </div>
          </div>
        </div>
      </div>
    );
  },
);

PlaygroundChatBar.displayName = 'PlaygroundChatBar';
